/**
 * The folds that are broken, marked on the template — and the sweep that
 * brings them in.
 *
 * ## Why it does not draw the folds
 *
 * "Show the seams" sounds like it means the fold lines, and it was measured
 * before it was built: **every region edge is a real fold**, 72 of 72 on both
 * garments. Eighteen rectangles, four edges each, every one continuing onto
 * another face — which is what a box is. So drawing "the seams" means outlining
 * all eighteen rectangles permanently over the artwork, on top of a template
 * guide that already delineates those same rectangles by colour at 35%.
 *
 * It draws the folds that are **wrong** instead. A clean garment gets no marks
 * at all, and that is what makes it safe to leave on: the control is silent
 * until it has something to report. See `docs/decisions/seam-overlay.md` §1.
 *
 * All 72 do get drawn while the Faces tool is active, because somebody who
 * picked that tool is already thinking in faces — it costs no new control.
 *
 * ## The sweep
 *
 * A wipe from left to right on the way in, and back out the way it came. It is
 * decoration, and it earns its place by doing one useful thing: a mark that
 * fades up everywhere at once reads as the artwork changing, while a mark that
 * arrives on a travelling edge reads as an overlay. That distinction matters
 * here more than usual, because these marks sit on top of somebody's drawing
 * and must never be mistaken for part of it.
 *
 * It is skipped entirely under `prefers-reduced-motion`, where the marks simply
 * appear. A flourish nobody asked for is exactly the kind of motion that
 * setting exists to switch off.
 */

import { CANVAS, REGION_IDS, type RegionId } from "../../shared/format";
import { REGIONS } from "../../shared/regions";

export interface SeamOverlayOptions {
  /** The paint canvas. The overlay tracks its box and its coordinate space. */
  canvas: HTMLCanvasElement;
  /** Texel indices of broken pairs, both ends, or null when there is no body. */
  marks: () => Int32Array | null;
  /** Whether the overlay is switched on at all. */
  enabled: () => boolean;
  /** True while the Faces tool is active — draw every fold, not just the broken. */
  allFolds: () => boolean;
  doc?: Document;
  /** Test seam: skip rAF and jump the sweep straight to its end state. */
  animate?: boolean;
}

export interface SeamOverlay {
  /** Redraw. Starts or reverses the sweep if the on/off state changed. */
  refresh(): void;
  destroy(): void;
}

const STYLE_ID = "seam-overlay-css";

const CSS = `
/*
  Same placement contract as .sym-overlay, and for the same reasons: absolutely
  positioned so the stage's single grid cell is not disturbed, and transparent
  to the pointer so it cannot swallow the first millimetre of a stroke.

  z-index above the symmetry guide. When both are up, a broken seam is the more
  urgent of the two — the guide describes a mode, this describes a defect.
*/
.seam-overlay {
  position: absolute;
  pointer-events: none;
  z-index: 3;
}
`;

/** How long the sweep takes, one way. */
const SWEEP_MS = 420;

/** How wide the leading edge is, in template pixels. */
const CREST = 46;

const BREAK = "#ff5a4d";
const FOLD = "#5b8cff";

/**
 * Which region owns each texel, as an index into `REGION_IDS`, or -1.
 *
 * Cached at module scope and keyed by size, the same shape `edge.ts` uses for
 * `rimDepths` and for the same reason: the alternative is eighteen rectangle
 * tests per mark per frame, and a sweep redraws ~25 times over 13,000 marks.
 * One pass builds it; every later question is an array read.
 */
let ownerCache: { width: number; height: number; owner: Int8Array } | null = null;

function regionOf(width: number, height: number): Int8Array {
  const hit = ownerCache;
  if (hit && hit.width === width && hit.height === height) return hit.owner;
  const owner = new Int8Array(width * height).fill(-1);
  for (let n = 0; n < REGION_IDS.length; n++) {
    const r = REGIONS[REGION_IDS[n] as RegionId];
    for (let y = r.y; y < r.y + r.h; y++) {
      if (y < 0 || y >= height) continue;
      for (let x = r.x; x < r.x + r.w; x++) {
        if (x < 0 || x >= width) continue;
        // First region wins, matching `regionAt`. They do not overlap.
        if (owner[y * width + x] === -1) owner[y * width + x] = n;
      }
    }
  }
  ownerCache = { width, height, owner };
  return owner;
}

/** How wide a mark is drawn, in texels. See `markRect`. */
export const MARK_PX = 2;

/**
 * Where a mark for this texel goes, clamped so it cannot leave its region.
 *
 * Exported and pure because the invariant it carries — **a mark never lands in
 * a gutter** — was a browser-only guarantee, and it was wrong: the first
 * version grew every mark up and to the left, which is inward on a right or
 * bottom edge and out into the 2px channel on a left or top one. 85 of 324 lit
 * pixels were outside every region, and they read as red speckle floating
 * between the faces with no artwork under it.
 *
 * Returns null for a texel no region owns, which a real mark never is: seams
 * are rim texels, and a rim texel is inside the rectangle whose rim it is.
 */
export function markRect(
  texel: number,
  width = CANVAS.width,
  height = CANVAS.height,
): { x: number; y: number; w: number; h: number } | null {
  const owner = regionOf(width, height);
  const which = owner[texel] ?? -1;
  if (which < 0) return null;
  const r = REGIONS[REGION_IDS[which] as RegionId];
  const x = texel % width;
  const y = Math.floor(texel / width);
  return {
    x: Math.min(Math.max(x - (MARK_PX - 1), r.x), r.x + r.w - MARK_PX),
    y: Math.min(Math.max(y - (MARK_PX - 1), r.y), r.y + r.h - MARK_PX),
    w: MARK_PX,
    h: MARK_PX,
  };
}

/** Ease-out cubic: quick off the mark, settles rather than stops. */
function ease(t: number): number {
  return 1 - (1 - t) ** 3;
}

export function mountSeamOverlay(opts: SeamOverlayOptions): SeamOverlay {
  const doc = opts.doc ?? document;
  const stage = opts.canvas.parentElement;
  if (!stage) throw new Error("seam overlay: the canvas has no stage to sit in");

  if (!doc.getElementById(STYLE_ID)) {
    const style = doc.createElement("style");
    style.id = STYLE_ID;
    style.textContent = CSS;
    doc.head.appendChild(style);
  }

  // Same guard as symmetry-overlay: `offsetLeft`/`offsetTop` are relative to
  // the nearest positioned ancestor, and the stage being one is a fact about a
  // stylesheet this file does not own.
  if (getComputedStyle(stage).position === "static") stage.style.position = "relative";

  const canvas = doc.createElement("canvas");
  canvas.className = "seam-overlay";
  canvas.setAttribute("aria-hidden", "true");
  canvas.width = CANVAS.width;
  canvas.height = CANVAS.height;
  stage.appendChild(canvas);

  const ctx = canvas.getContext("2d");

  /*
    Marks are built here and composited in one `drawImage`.

    Not an optimisation — a correctness requirement. Earlier this session an
    overlay drawn straight onto the shared surface double-composited at every
    region boundary, where two regions' bands overlap, and dropped luminance
    from 44 to 22 in a 4-texel band around all eighteen rectangles. Building on
    a scratch means overlapping marks merge instead of stacking.
  */
  const scratch = doc.createElement("canvas");
  scratch.width = CANVAS.width;
  scratch.height = CANVAS.height;
  const sctx = scratch.getContext("2d");

  const reduced = doc.defaultView?.matchMedia?.("(prefers-reduced-motion: reduce)");
  /*
    Three reasons to skip the sweep and jump to the end state, and the third is
    not an optimisation.

    `requestAnimationFrame` does not fire in a hidden document. The sweep is the
    only thing that advances `sweep`, so an overlay switched on — or mounted —
    while the tab is in the background would sit at 0 and draw NOTHING, then
    appear only if something happened to toggle it again after the tab came
    back. An animation nobody can watch is worth exactly nothing, and betting
    the overlay's existence on it is worth less than that.
  */
  const wantsAnimation = () => opts.animate !== false && !reduced?.matches && !doc.hidden;

  /** 0 hidden, 1 fully swept in. Where the wipe currently is. */
  let sweep = 0;
  let target = 0;
  let raf: number | null = null;
  let last = 0;

  function place() {
    canvas.style.left = `${opts.canvas.offsetLeft}px`;
    canvas.style.top = `${opts.canvas.offsetTop}px`;
    canvas.style.width = `${opts.canvas.offsetWidth}px`;
    canvas.style.height = `${opts.canvas.offsetHeight}px`;
  }

  /** Every fold: the outer ring of all eighteen regions. */
  function drawAllFolds(g: CanvasRenderingContext2D) {
    g.strokeStyle = FOLD;
    g.lineWidth = 1;
    g.globalAlpha = 0.5;
    for (const id of REGION_IDS) {
      const r = REGIONS[id];
      // Inset by half a pixel so a 1px stroke lands ON the rim row rather than
      // straddling it and covering two.
      g.strokeRect(r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1);
    }
    g.globalAlpha = 1;
  }

  /**
   * The broken texels themselves, two wide so they survive a zoomed-out view.
   *
   * One texel is invisible at fit-to-window zoom, which is where most people
   * sit — 585 across in a pane a few hundred pixels wide, so a single-texel
   * mark can land between two screen pixels and vanish.
   *
   * The widening is **clamped into the region**, and that is the whole of the
   * difficulty. The first version grew every mark up and to the left, with a
   * comment claiming it grew inward. That is inward on a right or bottom edge
   * and straight out into the gutter on a left or top one, which is most of
   * them: measured at **85 of 324 lit pixels outside every region**, drawn in
   * the 2px channel where Roblox renders nothing. It read as red speckle
   * floating between the faces, unattached to any artwork — reported as
   * "random red lines in the gutters", which is exactly what it was.
   *
   * A described invariant is not an enforced one. This one is enforced by
   * `regionOf`, so a mark cannot be drawn anywhere a texel does not exist.
   */
  function drawBreaks(g: CanvasRenderingContext2D, marks: Int32Array) {
    g.fillStyle = BREAK;
    for (let k = 0; k < marks.length; k++) {
      const r = markRect(marks[k] as number);
      if (r) g.fillRect(r.x, r.y, r.w, r.h);
    }
  }

  function render() {
    if (!ctx || !sctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (sweep <= 0) return;

    sctx.clearRect(0, 0, scratch.width, scratch.height);
    if (opts.allFolds()) drawAllFolds(sctx);
    const marks = opts.marks();
    if (marks && marks.length > 0) drawBreaks(sctx, marks);

    /*
      The wipe, as a mask on the finished marks rather than a clip while drawing
      them. Same reason the marks share a scratch: masking afterwards cannot
      change how two overlapping marks combine, and a clip applied per shape
      can.
    */
    const eased = ease(sweep);
    const front = eased * (CANVAS.width + CREST);
    if (sweep < 1) {
      sctx.globalCompositeOperation = "destination-in";
      const wipe = sctx.createLinearGradient(front - CREST, 0, front, 0);
      wipe.addColorStop(0, "rgba(0,0,0,1)");
      wipe.addColorStop(1, "rgba(0,0,0,0)");
      sctx.fillStyle = wipe;
      sctx.fillRect(0, 0, scratch.width, scratch.height);
      sctx.globalCompositeOperation = "source-over";
    }

    ctx.drawImage(scratch, 0, 0);

    // The crest: a bright bar riding the front of the wipe. Only while it is
    // actually travelling — a permanent bar would be a second thing to explain.
    if (sweep > 0 && sweep < 1) {
      const glow = ctx.createLinearGradient(front - CREST, 0, front, 0);
      glow.addColorStop(0, "rgba(255,255,255,0)");
      glow.addColorStop(0.75, "rgba(255,255,255,0.16)");
      glow.addColorStop(1, "rgba(255,255,255,0.55)");
      ctx.fillStyle = glow;
      ctx.fillRect(Math.max(0, front - CREST), 0, Math.min(CREST, front), canvas.height);
    }
  }

  function tick(now: number) {
    raf = null;
    const dt = last === 0 ? 16 : Math.min(64, now - last);
    last = now;
    const step = dt / SWEEP_MS;
    sweep = target > sweep ? Math.min(target, sweep + step) : Math.max(target, sweep - step);
    render();
    if (sweep !== target) {
      raf = requestAnimationFrame(tick);
    } else {
      last = 0;
    }
  }

  function settle() {
    const next = opts.enabled() ? 1 : 0;
    if (next === target && raf !== null) return;
    target = next;
    if (!wantsAnimation()) {
      sweep = target;
      render();
      return;
    }
    if (sweep === target) {
      render();
      return;
    }
    /*
      Reversing mid-sweep keeps the current position rather than restarting.
      Toggling twice quickly should look like one thing changing its mind, not
      two animations fighting.
    */
    if (raf === null) {
      last = 0;
      raf = requestAnimationFrame(tick);
    }
  }

  const observer = new ResizeObserver(() => {
    place();
    render();
  });
  observer.observe(opts.canvas);

  place();
  settle();

  return {
    refresh: settle,
    destroy() {
      if (raf !== null) cancelAnimationFrame(raf);
      observer.disconnect();
      canvas.remove();
    },
  };
}
